import { RichNodeElements } from '../types/common';

type PublicMetafield = {
  namespace: string;
  key: string;
};

// Add new metafields here
export const PublicMetafields: PublicMetafield[] = [
  { namespace: 'product', key: 'promotionalImage' },
  { namespace: 'product', key: 'promotionalCaption' },
  { namespace: 'product', key: 'campaignID' },
  { namespace: 'product', key: 'charity' },
  { namespace: 'product', key: 'promotion' },
  { namespace: 'product', key: 'gift' },
  { namespace: 'image', key: 'brand' },
  { namespace: 'size', key: 'height' },
  { namespace: 'size', key: 'width' },
  { namespace: 'size', key: 'depth' },
  { namespace: 'color', key: 'color' },
  { namespace: 'stock', key: 'moq' },
  { namespace: 'stock', key: 'totalsold' },
  { namespace: 'stock', key: 'inventory' },
  { namespace: 'date', key: 'launch' },
  { namespace: 'date', key: 'delivery' },
  { namespace: 'date', key: 'enddate' },
  { namespace: 'name', key: 'creator' },
  { namespace: 'date', key: 'petitionLaunchDate' },
  { namespace: 'date', key: 'petitionEndDate' },
  { namespace: 'date', key: 'paymentDue' },
  { namespace: 'product', key: 'petitionWhy' },
  { namespace: 'product', key: 'petitionStory' },
  { namespace: 'product', key: 'completeCollectionDiscountPercent' },
  { namespace: 'product', key: 'prideCharityName' },
  { namespace: 'product', key: 'pridePercentDonated' },
  { namespace: 'product', key: 'pinAddOn' },
  { namespace: 'product', key: 'disclaimer' },
  { namespace: 'product', key: 'hybridShippingInfo' },
  { namespace: 'product', key: 'lowStockThreshold' },
];

// Collection metafields here
export const CollectionMetafields: PublicMetafield[] = [
  { namespace: 'global', key: 'isCreatorCollection' },
  { namespace: 'global', key: 'isMakeshiftCollection' },
  { namespace: 'global', key: 'isCreatorBranded' },
  { namespace: 'global', key: 'navTheme' },
  { namespace: 'global', key: 'creatorMessage' },
  { namespace: 'global', key: 'creatorAvatar' },
  { namespace: 'global', key: 'creatorLogo' },
  { namespace: 'global', key: 'mediaEmbed' },
  { namespace: 'global', key: 'backgroundColour' },
  { namespace: 'global', key: 'headerBackground' },
  { namespace: 'global', key: 'creatorLogo' },
  { namespace: 'global', key: 'socialLinks' },
  { namespace: 'global', key: 'countdownBackgroundColour' },
  { namespace: 'global', key: 'countdownTextColour' },
  { namespace: 'global', key: 'previewNavTheme' },
  { namespace: 'global', key: 'previewCreatorMessage' },
  { namespace: 'global', key: 'previewCreatorAvatar' },
  { namespace: 'global', key: 'previewCreatorLogo' },
  { namespace: 'global', key: 'previewMediaEmbed' },
  { namespace: 'global', key: 'previewBackgroundColour' },
  { namespace: 'global', key: 'previewHeaderBackground' },
  { namespace: 'global', key: 'previewCreatorLogo' },
  { namespace: 'global', key: 'previewSocialLinks' },
  { namespace: 'global', key: 'previewCountdownBackgroundColour' },
  { namespace: 'global', key: 'previewCountdownTextColour' },
  { namespace: 'global', key: 'cycPromoMessage' },
  { namespace: 'global', key: 'productDropCollections' },
  { namespace: 'global', key: 'stretchGoalsData' },
  { namespace: 'global', key: 'stretchGoalsImages' },
  { namespace: 'global', key: 'stretchGoalsTheme' },
  { namespace: 'global', key: 'shouldEnableStretchGoals' },
  { namespace: 'global', key: 'isProductDropCollection' },
];

// Variant metafields here
export const VariantMetafields: PublicMetafield[] = [
  { namespace: 'date', key: 'shipByDate' },
  { namespace: 'variant', key: 'lowStockThreshold' },
];

type RichTextNode = {
  type: string;
  value?: string;
  bold?: boolean;
  listType?: 'unordered' | 'ordered';
  level?: number;
  children?: RichTextNode[];
};

type RichTextRoot = {
  type: 'root';
  children: RichTextNode[];
};

function renderNode(node: RichTextNode): string {
  switch (node.type) {
    case RichNodeElements.Text: {
      let content = escapeHtml(node.value || '');
      if (node.bold) {
        content = `<strong>${content}</strong>`;
      }
      return content;
    }

    case RichNodeElements.Paragraph:
      return `<p>${(node.children || []).map(renderNode).join('')}</p>`;

    case RichNodeElements.Heading: {
      const level = Math.min(Math.max(node.level || 2, 1), 6); // Clamp to h1–h6
      return `<h${level}>${(node.children || []).map(renderNode).join('')}</h${level}>`;
    }

    case RichNodeElements.List: {
      const tag = node.listType === 'ordered' ? 'ol' : 'ul';
      return `<${tag}>${(node.children || []).map(renderNode).join('')}</${tag}><br/>`; // Adds a break to give spacing
    }

    case RichNodeElements.ListItem:
      return `<li>${(node.children || []).map(renderNode).join('')}</li>`;

    default:
      return (node.children || []).map(renderNode).join('');
  }
}

// Escapes text to prevent HTML injection
function escapeHtml(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// Main parser
export function parseShopifyRichText(json: string): string {
  const root: RichTextRoot = JSON.parse(json);
  if (root.type !== 'root' || !Array.isArray(root.children)) return '';

  return root.children.map(renderNode).join('\n');
}
